import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";
import { CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { CuteLoader } from "@/components/CuteLoader";

export default function ManagerApprovalsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const { data } = await api.get("/pto-requests?status=pending");
      setRequests(data);
    } catch (error) {
      console.error('Failed to load PTO requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await api.patch(`/pto-requests/${requestId}`, {
        status: "approved",
        reviewed_by: user._id,
        notes: ""
      });
      loadRequests();
    } catch (error) {
      console.error('Failed to approve request:', error);
      alert('Failed to approve request');
    }
  };

  const handleDeny = async (requestId) => {
    try {
      await api.patch(`/pto-requests/${requestId}`, {
        status: "denied",
        reviewed_by: user._id,
        notes: ""
      });
      loadRequests();
    } catch (error) {
      console.error('Failed to deny request:', error);
      alert('Failed to deny request');
    }
  };

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="border-b border-border bg-card px-4 py-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground flex items-center gap-2">
          Approvals
          {requests.length > 0 && (
            <Badge variant="destructive" className="text-sm">{requests.length}</Badge>
          )}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Pending PTO requests</p>
      </div>

      <div className="p-4 space-y-4">
        {requests.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <p className="text-muted-foreground">No pending approvals</p>
            </CardContent>
          </Card>
        ) : (
          requests.map((req) => (
            <Card key={req._id || req.id}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  PTO Request
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Employee</span>
                    <span className="font-medium">{req.employee_name || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dates</span>
                    <span className="font-medium">
                      {formatDate(req.start_date)} - {formatDate(req.end_date)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hours</span>
                    <span className="font-medium">{req.hours} hrs</span>
                  </div>
                  {req.reason && (
                    <div>
                      <span className="text-muted-foreground">Reason:</span>
                      <p className="text-sm mt-1">{req.reason}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleApprove(req._id || req.id)}
                    className="flex-1"
                    variant="default"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button 
                    onClick={() => handleDeny(req._id || req.id)}
                    className="flex-1"
                    variant="destructive"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Deny
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
